import type { Hazard, ValuesByYear, Year } from "@/features/pe_aldd";

export type AnomalyManagamentSupportedHazards = {
  coastal_inundation: number | null;
  cyclone_storm_surge: number | null;
  flood_riverine: number | null;
  flood_surfacewater: number | null;
};

export type DefenceSupportedHazards = {
  coastal_inundation: "defended" | "undefended";
  cyclone_storm_surge: "defended" | "undefended";
  flood_riverine: "defended" | "undefended";
  flood_surfacewater: "defended" | "undefended";
};

// Adapted from "get_top_two_hazards_for_year" in Facades.
export const getSignificantHazards = (byHazard: Record<Hazard, ValuesByYear>, year: Year): Hazard[] => {
  return Object.entries(byHazard)
    .filter(([, values]) => {
      const value = values[year];
      return value !== null && value >= 0.002;
    })
    .slice(0, 2)
    .map(([hazard]) => hazard as Hazard);
};

export const HAZARD_DESCRIPTIONS: Record<Hazard, string> = {
  coastal_inundation:
    "Sea water flooding due to high tides, wind, low air pressure and waves. Inundation can damage coastal land, infrastructure and buildings.",
  cyclone_storm_surge:
    "Tropical cyclone storm surge is the abnormal rise in sea level that occurs when a tropical cyclone (such as a hurricane or typhoon) makes landfall or moves over coastal areas. This surge is caused primarily by the strong winds of the cyclone, which push large volumes of water toward the shore, raising the normal sea level.",
  cyclone_wind:
    "Tropical Cyclones (also known as hurricanes or typhoons) are complex weather systems that are driven by extreme low-pressure cells forced by high sea surface temperatures (SSTs) in the tropics. Extreme winds speeds caused by tropical cyclones can cause significant loss of life and asset damage.",
  flood_riverine:
    "Changes in precipitation in a catchment that causes a river to exceed its capacity, inundating nearby areas. Riverine (Fluvial) flooding can damage low-lying building or infrastructure assets.",
  flood_surfacewater:
    "Increased frequency of extreme rainfall leading to overland flooding. Surface Water (Pluvial) flooding can damage low-lying building or infrastructure assets.",
  forest_fire:
    "A destructive fire that spreads via trees and forest. Flames and heat from burning vegetation can damage buildings and infrastructure. Increased incidence of fire weather due to confluence of days with higher temperatures, high wind speeds and drier conditions.",
  freeze_thaw:
    "Changes in the annual freeze and thaw cycles resulting from winter periods that trend close to freezing point. Saturated building materials may freeze, expand and crack facades and structural elements.",
  heat: "Extreme temperatures can cause electrical and mechanical components to exceed their design temperature and fail or send spurious signals.",
  landslide:
    "Landslides are natural disasters where the ground moves downhill because of gravity, taking soil, rocks, or anything that comes along the way. Landslides are very destructive, causing loss of life and destruction of property and infrastructure resulting in significant financial damage. The landslide model looks at the probability of increased precipitation induced landslides.",
  soil_movement: "Soil contraction due to less rainfall causing subsidence damage to structures.",
  wind: "Changes in wind regimes, sea surface temperature and wind speeds. High-wind conditions that may exceed a building’s design specifications.",
};
